import React, { useState } from 'react';
import PropTypes from 'prop-types';
import LabelPill from '../../../shared/components/pill/LabelPill';
import { PILL_SIZES } from '../../../shared/components/pill/tones';

const MAX_NAME_LENGTH = 16;

function truncate(name) {
  if (name.length <= MAX_NAME_LENGTH) return name;
  return `${name.slice(0, MAX_NAME_LENGTH)}…`;
}

// Tags are the one label that carries the accent: they are what an admin collects, searches and
// scopes by. An auto-assigned tag is a different kind of fact — the system put it there — so it
// steps back to neutral and says so with the wand.
function Chip({ tag, auto, removable, size, onRemove }) {
  return (
    <LabelPill
      tone={auto ? 'neutral' : 'accent'}
      size={size}
      icon={auto ? 'bi-magic' : undefined}
      title={auto ? `${tag.name} — assigned automatically` : (tag.name.length > MAX_NAME_LENGTH ? tag.name : undefined)}
      onRemove={removable ? () => onRemove(tag) : undefined}
      removeLabel={`Remove ${tag.name}`}
    >
      {truncate(tag.name)}
    </LabelPill>
  );
}

Chip.propTypes = {
  tag: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), name: PropTypes.string }).isRequired,
  auto: PropTypes.bool,
  removable: PropTypes.bool,
  size: PropTypes.oneOf(PILL_SIZES),
  onRemove: PropTypes.func,
};

Chip.defaultProps = {
  auto: false,
  removable: false,
  size: 'standalone',
  onRemove: undefined,
};

export default function TagChipList({ tags, maxVisible, autoTagIds, removable, size, onRemove }) {
  const [expanded, setExpanded] = useState(false);

  if (!tags || tags.length === 0) return null;

  const visible = expanded ? tags : tags.slice(0, maxVisible);
  const overflowCount = tags.length - visible.length;

  return (
    <div className="d-flex flex-wrap align-items-center gap-1">
      {visible.map((tag) => (
        <Chip
          key={tag.id}
          tag={tag}
          auto={autoTagIds.includes(tag.id)}
          removable={removable && !autoTagIds.includes(tag.id)}
          size={size}
          onRemove={onRemove}
        />
      ))}
      {/* Not a pill: this one does something when clicked, and a pill that looks like its
          neighbours but acts differently is the confusion the whole system is meant to avoid. */}
      {overflowCount > 0 && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 ms-1 small text-muted text-decoration-none"
          onClick={() => setExpanded(true)}
        >
          +{overflowCount} more
        </button>
      )}
    </div>
  );
}

TagChipList.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired,
  })),
  maxVisible: PropTypes.number,
  autoTagIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  removable: PropTypes.bool,
  size: PropTypes.oneOf(PILL_SIZES),
  onRemove: PropTypes.func,
};

TagChipList.defaultProps = {
  tags: [],
  maxVisible: 5,
  autoTagIds: [],
  removable: false,
  size: 'standalone',
  onRemove: undefined,
};
